/**
 * Tree viewport and scroll navigation
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { Viewport } from '../types';

export function useTreeNavigation() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Viewport>({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateViewport = () => {
      setViewport({
        x: container.scrollLeft,
        y: container.scrollTop,
        width: container.clientWidth,
        height: container.clientHeight
      });
    };

    updateViewport();
    container.addEventListener('scroll', updateViewport);
    const resizeObserver = new ResizeObserver(updateViewport);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateViewport);
      resizeObserver.disconnect();
    };
  }, []);

  const navigateTo = useCallback((x: number, y: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: x, top: y, behavior: 'smooth' });
    }
  }, []);

  const centerOn = useCallback((x: number, y: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: x - viewport.width / 2,
        top: y - viewport.height / 2,
        behavior: 'smooth'
      });
    }
  }, [viewport]);

  return { scrollContainerRef, viewport, navigateTo, centerOn };
}
